import OT from "@opentok/client";
import { useDispatch, useSelector } from "react-redux";

import { useCallback, useEffect, useRef, useState } from "react";

import { SidebarContent } from "components/Meeting/Live/VideoProvider";

import { useAppSelector } from "store/hooks";
import {
  LiveParticipantType,
  LiveRoomAccessDetails,
} from "store/meetings/types";
import { SingleVoting } from "store/voting/types";

import {
  addMessage,
  removeAllMessagesByMeetingId,
} from "../../store/chat/reducer";
import { RetryChatMessage, SignalEvent } from "../../store/chat/types";
import { RootState } from "../../store/store";
import { saveChatMessage } from "../../services/chatApi";

const useSession = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [session, setSession] = useState<OT.Session | null>(null);
  const [connected, setConnected] = useState(false);
  const [subscribers, setSubscribers] = useState<OT.Subscriber[]>([]);
  const [subscribersCount, setSubscribersCount] = useState<number>(0);
  const sessionRef = useRef<OT.Session | null>(null);
  const [sidebarContent, setSidebarContent] = useState<SidebarContent>(null);
  const [agendaOpened, setAgendaOpened] = useState(false);
  const [agendaSmall, setAgendaSmall] = useState(false);
  const [liveVoting, setLiveVoting] = useState<SingleVoting | null>(null);
  const [liveParticipants, setLiveParticipants] = useState<
    LiveParticipantType[]
  >([]);
  const [remoteScreenShareEnabled, setRemoteScreenShareEnabled] =
    useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: participant } = useAppSelector((state) => state.participant);
  const dispatch = useDispatch();
  const apiKey = process.env.REACT_APP_VONAGE_API_KEY;
  const [failedMessages, setFailedMessages] = useState<RetryChatMessage[]>([]);

  useEffect(() => {
    setSubscribersCount(subscribers.length);
  }, [subscribers]);

  // Remote screen share handling
  useEffect(() => {
    const isSharing = subscribers.some(
      (subscriber) => subscriber?.stream?.videoType === "screen"
    );
    setRemoteScreenShareEnabled(isSharing);
    //If someone is sharing screen, we are finding div with that screen by streamID and
    //moving it to div with id #remote-screen-share-container to style it separately outside of normal grid
    if (isSharing) {
      const screenShareDiv = document.getElementById(
        subscribers.find(
          (subscriber) => subscriber.stream?.videoType === "screen"
        )?.id!
      );
      if (screenShareDiv) {
        const remoteScreenShareContainer = document.getElementById(
          "remote-screen-share-container"
        );
        remoteScreenShareContainer?.appendChild(screenShareDiv);
      }
    }
  }, [subscribers]);

  const onConnectionCreated = useCallback(
    (event: { connection: OT.Connection }) => {
      if (!event?.connection?.data) {
        console.warn("Connection data is missing!");
      }

      const dataParts = event?.connection?.data?.split("=");

      if (dataParts?.length < 2) {
        console.warn("Invalid connection data format:", event.connection.data);
        return;
      }

      const lPdata = {
        connectionId: event?.connection?.connectionId,
        userId: dataParts[1],
      };

      setLiveParticipants((prev: LiveParticipantType[]) => [
        ...prev.filter((connection) => connection.userId !== lPdata?.userId),
        lPdata,
      ]);
    },
    [user]
  );

  const onStreamCreated = useCallback(
    (event: { stream: OT.Stream }) => {
      const subscriber = sessionRef.current?.subscribe(
        event.stream,
        "subscriber",
        {
          width: "100%",
          height: "100%",
          showControls: false,
          insertMode: "append",
        },
        (err) => err && console.error(err)
      );

      if (
        subscriber &&
        sessionRef.current?.connection?.connectionId &&
        user?.name
      ) {
        // Request stream info from the new participant
        sessionRef.current.signal(
          {
            type: "request-stream-info",
            to: subscriber?.stream?.connection,
          },
          (error) => {
            if (error) {
              console.error("Error requesting stream-info:", error);
            }
          }
        );
      }
      subscriber && setSubscribers((prev) => [...prev, subscriber]);
    },
    [sessionRef, user]
  );

  const onConnectionDestroyed = useCallback(
    (event: { connection: OT.Connection }) => {
      setLiveParticipants((prev: LiveParticipantType[]) => [
        ...prev.filter(
          (user: LiveParticipantType) =>
            user?.userId !== event?.connection?.data?.split("=")[1]
        ),
      ]);
    },
    []
  );

  const onStreamDestroyed = useCallback((event: { stream: OT.Stream }) => {
    setSubscribers((prev) =>
      prev.filter(
        (subscriber) => subscriber?.stream?.streamId !== event?.stream?.streamId
      )
    );
  }, []);

  const connect = useCallback(
    (accessDetails: LiveRoomAccessDetails) => {
      setIsConnecting(true);

      const { sid, access_token } = accessDetails;

      if (apiKey && sid && access_token) {
        sessionRef.current = OT.initSession(apiKey, sid);

        const eventHandlers = {
          sessionConnected: () => {
            console.log("Session Connected!");
            setConnected(true);
          },
          sessionDisconnected: () => {
            console.log("Session Disconnected!");
            setConnected(false);
          },
          connectionCreated: onConnectionCreated,
          connectionDestroyed: onConnectionDestroyed,
          // streamCreated: onStreamCreated,
          // streamDestroyed: onStreamDestroyed,
          signal: receiveMessage,
        };
        sessionRef.current.on(eventHandlers);

        sessionRef.current.connect(access_token, (err) => {
          if (err) {
            console.error(err);
            setIsConnecting(false);
          } else {
            console.log("Session Connected!");
            setConnected(true);
            setSession(sessionRef.current);
            setIsConnecting(false);
          }
        });
      }
    },
    [
      onStreamCreated,
      onStreamDestroyed,
      apiKey,
      onConnectionCreated,
      onConnectionDestroyed,
      user,
    ]
  );

  const disconnect = useCallback(() => {
    if (sessionRef.current) {
      subscribers.forEach((subscriber) => {
        sessionRef.current?.unsubscribe(subscriber);
      });
      sessionRef.current.off();
      sessionRef.current.disconnect();
      sessionRef.current = null;
      setSession(null);
      setConnected(false);
      setSubscribers([]);
      setLiveParticipants([]);
      setSubscribersCount(0);
    }
  }, [
    sessionRef,
    setSession,
    setConnected,
    setSubscribers,
    setSubscribersCount,
    subscribers,
  ]);

  const receiveMessage = (event: SignalEvent) => {
    try {
      if (!event.data) {
        console.warn("Received an empty message.");
        return;
      }
      const message = JSON.parse(event.data);
      if (!message?.text) {
        console.warn("Message is empty after trimming.");
        return;
      }
      const senderId = event.from?.connectionId || "unknown";
      const sender = message.sender;
      dispatch(
        addMessage({
          meetingId: message.meetingId,
          message: {
            from: senderId,
            text: message.text,
            timestamp: message.timestamp || Date.now(),
            sender: {
              name: sender?.name || "Unknown",
              id: sender?.id || "unknown",
              email: sender?.email || "unknown",
            },
          },
        })
      );
    } catch (error) {
      console.error("Error receiving message:", error);
    }
  };

  const sendMessage = useCallback(
    (text: string, meetingId: string, session: OT.Session) => {
      if (!session) {
        console.log("No session found");
        return;
      }
      const name =
        user?.name ||
        localStorage.getItem("loggedUser") ||
        localStorage.getItem("guestName") ||
        "Guest";

      //  user?.name || localStorage.getItem("loggedUser") === ""
      //               ?
      //               : localStorage.getItem("loggedUser") || "Guest",
      const message = {
        from: session.connection?.connectionId,
        text,
        timestamp: Date.now(),
        meetingId: meetingId,
        sender: {
          name: name,
          id:
            user?.id || localStorage.getItem("loggedUser") === ""
              ? localStorage.getItem("guestName")
              : localStorage.getItem("loggedUser") || "Guest",
          email:
            user?.email || localStorage.getItem("loggedUser") === ""
              ? localStorage.getItem("guestName")
              : localStorage.getItem("loggedUser") || "Guest",
        },
      };
      try {
        const attemptSend = (retryCount = 0) => {
          session.signal(
            { type: "chat", data: JSON.stringify(message) },
            (err) => {
              if (err) {
                console.error("Error sending message:", err);
                if (retryCount < 5) {
                  setTimeout(
                    () => attemptSend(retryCount + 1),
                    Math.pow(2, retryCount) * 1000
                  );
                } else {
                  setFailedMessages((prev) => [
                    ...prev,
                    {
                      text,
                      meetingId,
                      session,
                    },
                  ]);
                }
              } else {
                console.log("Message sent successfully");
              }
            }
          );
        };

        attemptSend();

        // Save message to backend (fire and forget, don't block UI)
        saveChatMessage(meetingId, {
          message_text: text,
          timestamp: message.timestamp,
          sender: {
            id: user?.id
              ? String(user.id)
              : session.connection?.connectionId || "guest",
            name: message.sender.name,
            email: user?.email || null,
          },
          connection_id: session.connection?.connectionId,
        }).catch((error) => {
          console.error("Failed to save message to backend:", error);
        });
      } catch (error) {
        console.error("Message send failed, adding to retry queue:", error);
        setFailedMessages((prev) => [
          ...prev,
          {
            text,
            meetingId,
            session,
          },
        ]);
      }
    },
    []
  );

  const retryFailedMessages = useCallback(() => {
    setFailedMessages((prev) => {
      prev.forEach((message) => {
        sendMessage(message.text, message.meetingId, message.session);
      });
      return [];
    });
  }, [sendMessage]);

  return {
    session,
    connected,
    connect,
    disconnect,
    isConnecting,
    subscribers,
    subscribersCount,
    sidebarContent,
    setSidebarContent,
    agendaOpened,
    setAgendaOpened,
    agendaSmall,
    setAgendaSmall,
    liveVoting,
    setLiveVoting,
    remoteScreenShareEnabled,
    liveParticipants,
    sendMessage,
    receiveMessage,
    failedMessages,
    retryFailedMessages,
    setSubscribers,
  };
};

export default useSession;
